import { useLocation, type Location } from "react-router-dom";

import { useUser } from "@components/user-context";
import { type Logger, loggerConfig } from "@internal/analytics";
import { type User } from "@internal/user";
import { type AmplitudeEventContainer } from "@north-beam/nb-client-side/analytics";
import { createLoggerHook } from "@north-beam/nb-client-side/hooks";

type Options = Partial<{
  readonly container: AmplitudeEventContainer;
}>;

const rootUseLogger = createLoggerHook<User>(loggerConfig);

type HookLogger<O extends Options> = Logger<O & { location: Location }>;

export function useLogger<O extends Options>(config: O): HookLogger<O>;

export function useLogger<O extends Options>(
  name: string,
  config: O,
): HookLogger<O>;

export function useLogger<O extends Options>(
  arg0?: string | O,
  arg1?: O,
): HookLogger<O> {
  const location: Location = useLocation();
  const { user } = useUser();

  let name: string | undefined = undefined;
  let options: O = {} as O;
  if (typeof arg0 === "string") {
    name = arg0;
    if (arg1 === undefined) {
      throw new TypeError("Invalid function signature implementation!");
    }
    options = arg1;
  } else if (arg0) {
    options = arg0 as O;
  }
  // TODO: Figure out how to get previous location.
  return rootUseLogger<O & { location: Location }>(name, user, {
    ...options,
    location,
  });
}
